import { IO } from "@grapecity/spread-excelio";
import GC from "@grapecity/spread-sheets";
import "@grapecity/spread-sheets-charts";

import { SpreadSheets, Worksheet } from "@grapecity/spread-sheets-react";
import { AxiosResponse } from "axios";
import * as React from "react";
import "../../../indexStyles.css";
import DeliverableService from "../../../features/DeliverableView/services/DeliverableService";
import CoaService from "../../../features/CoaView/services/CoaService";
import TemplateService from "../../../features/TemplateView/services/TemplateService";
import {
  IMPORT_FAILED_MESSAGE,
  IMPORT_SUCCESS_MESSAGE,
  IMPORT_TASK_PENDING_MESSAGE,
  IS_SHEET_CHANGED,
  NAME_KEY,
  SAVE_TASK_PENDING_MESSAGE,
  SAVE_SUCCESS,
  SAVE_ERROR,
  TASK_POLLING_INTERVAL,
  TEMPLATE,
  TaskStatus,
  TaskType,
} from "../../utils/CommonConstants";
import { StyledClose } from "../DeliverableMenuComponent/DeliverableMenuComponent.styles";
import { PopUp } from "../../../shared/base-components/pop-up-dialog-box/PopUp";
import { BusyIndicator } from "../../../shared/base-components/react-loader/busy-indicator/BusyIndicator";
import { Button, message } from "antd";
import { SpreadSearch } from "../../../shared/base-components/Search/SpreadsheetSearch";

(window as any).GC = GC;

// Define loader component to display during data fetching
const LoaderComponent: React.FC = () => (
  <div className="loader-container">
    <div className="loader">
      <BusyIndicator />
    </div>
  </div>
);

export class SpreadImport extends React.Component<any, any> {
  spread: GC.Spread.Sheets.Workbook | null;
  spreadRef: React.RefObject<any>;
  deliverableService = new DeliverableService();
  coaService = new CoaService();
  templateService = new TemplateService();
  previousSheet: any;
  nextSheet: any;
  dirtyRowData: Record<number, any>;
  poll: any;
  statusBar: GC.Spread.Sheets.StatusBar.StatusBar | null = null;

  constructor(props: any) {
    super(props);
    this.spread = null;
    this.spreadRef = React.createRef();
    this.previousSheet = null;
    this.nextSheet = null;
    this.dirtyRowData = {};
    this.openFile = this.openFile.bind(this);
    this.state = {
      saveButtonLoading: false,
      importButtonLoading: false,
      displayPopup: false,
      chartOfAccountsChanged: false,
      partnerDataChanged: false,
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      refreshPage: false,
      loading: false,
      pendingTabChange: null,
      showOverlay: false,
      pollTimer: null,
      taskStatusMessage: IMPORT_TASK_PENDING_MESSAGE,
    };
  }

  componentDidMount() {
    this.loadExcel().then(() => {
      this.handleActiveTabChange();
    });
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    const { activeTab, location, navigate } = this.props;
    const currentPath = window.location.pathname;
    const state = location.state;
    const prevLocationState = prevProps.location.state;
    // Check if the currentPath has changed and refresh accordingly
    if (
      (currentPath.includes("/deliverable") &&
        currentPath.includes("template")) ||
      (prevState.currentPath.includes("/deliverable") &&
        prevState.currentPath.includes("template"))
    ) {
      if (currentPath !== prevState.currentPath) {
        this.setState({ currentPath }, () => {
          this.loadExcel();
        });
      }
    } else if (activeTab !== prevProps.activeTab && !this.state.displayPopup) {
      this.handleActiveTabChange();
      this.refreshComponent(navigate);
    }

    if (state?.refreshPage && !prevLocationState?.refreshPage) {
      this.refreshComponent(navigate);
    }
  }

  componentWillUnmount() {
    this.clearPollingTimer();
  }

  clearPollingTimer() {
    if (this.state.pollTimer) {
      clearInterval(this.state.pollTimer);
      this.setState({ pollTimer: null });
    }
  }

  refreshComponent(navigate: any) {
    this.loadExcel();

    const newState = { ...this.props.location.state, refreshPage: false };
    navigate({ ...this.props.location, state: newState }, { replace: true });
  }

  render() {
    const { loading, showOverlay } = this.state;

    return (
      <div className="sample-tutorial">
        {loading && <LoaderComponent />}
        {showOverlay && this.renderOverlay()}
        <div id="formulaContainer">
          <div id="formulaTextBoxContainer"></div>
        </div>
        <div className="sample-spreadsheets">
          <SpreadSearch spread={this.spread} />
          {(this.state.saveButtonLoading || this.state.importButtonLoading) && (
            <div className="loader"></div>
          )}
          {this.state.displayPopup && (
            <div className="loader">
              <div className="popup-container">
                <div className="close-button">
                  <StyledClose
                    size={22}
                    onClick={this.closePopupWithoutSaving}
                  />
                </div>
                <div className="popup-content">
                  <p>{IS_SHEET_CHANGED}</p>
                </div>
                <div className="button-container">
                  <div>
                    <Button
                      id="save-file"
                      type="primary"
                      onClick={(e) => this.saveClick(e, true)}
                    >
                      Save
                    </Button>
                  </div>
                  <div>
                    <Button
                      id="discard-changes"
                      type="default"
                      onClick={this.discardChanges}
                    >
                      Discard
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div id="statusBar" style={{ height: "30px" }} />
          <div style={{ height: "30px", background: "#fff" }} />
          <div
            className="spread-wrapper"
            style={{
              height: "calc(100dvh - 175px)", // full container height
              display: "flex",
              flexDirection: "row",
              alignItems: "stretch", // children stretch to full height
            }}
          >
            {/* Left spacer: fixed width, full height */}
            <div
              style={{
                width: "20px",
                minWidth: "20px",
                height: "100%",
                background: "#fff", // for testing visibility
                flexShrink: 0, // prevent it from shrinking
              }}
            ></div>

            {/* Main SpreadSheets container: fills remaining space */}
            <div
              style={{
                flex: 1, // take remaining width
                height: "100%", // ensure it fills parent height
                minHeight: 0, // required in flex for scrollable children
              }}
            >
              <SpreadSheets
                ref={this.spreadRef}
                valueChanged={(type, args) =>
                  this.handleValueChanged(type, args)
                }
                workbookInitialized={(spread: any) => this.initSpread(spread)}
              >
                <Worksheet />
              </SpreadSheets>
            </div>
          </div>
        </div>
        {/* <div className="option-row">
          <input
            type="button"
            id="Save"
            disabled={this.state.saveButtonLoading}
            defaultValue="Save"
            className="button"
            onClick={(e) => this.saveClick(e)}
          />
        </div> */}
      </div>
    );
  }

  handleValueChanged = (type: any, args: any) => {
    try {
      const spreadsheet = args?.spreadsheet || this.spread;
      const sheet = spreadsheet.getActiveSheet();
      const { row, col } = args || {};
      const cell = sheet.getCell(row, col);
      const newValue = cell?.value() ?? null;
      const oldValue = args.oldValue;
      const isFormula = String(newValue).startsWith("=");
      const worksheetJson = sheet.toJSON();
      const cellDetails = worksheetJson.data.dataTable[row][col] || {};
      const updatedCellDetails = { ...cellDetails };

      if (isFormula) {
        updatedCellDetails.value = oldValue;
        updatedCellDetails.formula = newValue;
      } else {
        updatedCellDetails.value = newValue;
        delete updatedCellDetails.formula;
      }

      worksheetJson.data.dataTable[row][col] = updatedCellDetails;
      sheet.fromJSON(worksheetJson);
      spreadsheet.resumeCalcService(true);
      this.setState({ displayPopup: false });
    } catch (error) {
      console.error("Error in handleValueChanged:", error);
    }
  };

  renderOverlay() {
    const { showOverlay, showOkButton } = this.state;
    const onOkButtonClick = () => {
      this.setState({ showOverlay: false });
    };

    return (
      <div className="overlay">
        <div className="overlay-content">
          <PopUp
            isActive={showOverlay}
            isButtonDisabled={!showOkButton}
            message={this.state.taskStatusMessage}
            onOKClick={onOkButtonClick}
          />
          <div className="loader"></div>
        </div>
      </div>
    );
  }
  handleActiveTabChange = () => {
    const { activeTab } = this.props;
    const search = window.location.search;
    const spread: any = this.spread;
    const currentSheet = spread?.getActiveSheet();
    const dirtyRows = currentSheet.getDirtyRows();

    if (dirtyRows.length > 0 && !search.includes("multiSheet")) {
      // Show save/discard popup and store the intended tab change
      this.setState({
        displayPopup: true,
        pendingTabChange: activeTab,
      });
      return; // Prevent immediate tab change
    }

    // for (var row = 0; row < rowCount; row++) {
    //   this.spread?.getActiveSheet().autoFitRow(row);
    // }

    // Proceed with tab change if there are no dirty rows
    this.performTabChange(activeTab);
  };

  performTabChange = (tabName: string) => {
    const spread: any = this.spread;
    const workbookJson = spread?.toJSON();
    const { sheets } = workbookJson;

    const sheetNames = Object.keys(sheets);

    const activeSheetIndex = sheetNames.indexOf(tabName);
    console.log("Sheet Names:", sheetNames);
    console.log("Active Tab:", tabName);
    console.log("Active Sheet Index:", activeSheetIndex);
    spread.setActiveSheetIndex(activeSheetIndex === -1 ? 0 : activeSheetIndex);
    this.setState({ saveButtonLoading: false, pendingTabChange: null });
  };

  openFile = (selectorFiles: FileList) => {
    const spread: any = this.spread;
    this.setState({ loading: true });
    const path = window.location.pathname;
    let excelIo = new IO();
    excelIo.open(selectorFiles[0], (json: any) => {
      // Retrieve activeTab from props
      const { activeTab } = this.props;

      // Find the sheet index based on activeTab
      const sheetNames = Object.keys(json.sheets);
      let activeSheetIndex = sheetNames.indexOf(activeTab);

      // If the activeTab is not found, set the default active sheet as the first sheet
      if (activeSheetIndex === -1) {
        activeSheetIndex = 0;
      }

      let workbookObj = { ...json, activeSheetIndex };
      if (path.includes("/deliverable") && !path.includes("/template")) {
        spread.fromJSON(workbookObj, {
          ignoreFormula: true,
          // frozenColumnsAsRowHeaders:false,
          // frozenRowsAsColumnHeaders:false,
          doNotRecalculateAfterLoad: true,
        });
        spread.options.calcOnDemand = true;
      } else {
        spread.fromJSON(workbookObj, {
          doNotRecalculateAfterLoad: true,
        });
      }
      // spread.options.tabEditable = false;
      // spread.options.newTabVisible = false;
      spread.options.allowContextMenu = false;
      spread.options.showRowHeader = false;
      spread.options.showColumnHeader = false;

      const sheet = spread.getActiveSheet();
      sheet.options.rowHeaderVisible = false;
      sheet.options.colHeaderVisible = false;
      sheet.frozenRowCount(0);
      sheet.frozenColumnCount(0);
      this.setState({ loading: false });

      if (this.spread?.options) {
        const activeSheet = this.spread.getActiveSheet();
        this.spread.options.allowUserEditFormula = false;
        for (
          let col = 1;
          col <= spread.getActiveSheet().getColumnCount();
          col++
        ) {
          //Commenting the code to check column autofit and width issue.
          // activeSheet.autoFitColumn(col);
          activeSheet.setColumnWidth(
            col,
            //activeSheet.getColumnWidth(col) * 1.08
            activeSheet.getColumnWidth(col),
          );
        }
        // for (
        //   let row = 0;
        //   row < this.spread.getActiveSheet().getRowCount();
        //   row++
        // ) {
        //   this.spread.getActiveSheet().autoFitRow(row);
        // }
      }

      if (path.includes("/template")) {
        spread.options.tabStripVisible = true;
      } else if (path.includes("/deliverable")) {
        spread.options.tabStripVisible = false;
        const activeSheet = spread.getActiveSheet();
        activeSheet.options.isProtected = false;
        // for (
        //   let col = 1;
        //   col <= spread.getActiveSheet().getColumnCount();
        //   col++
        // ) {
        //   if (
        //     (spread.getActiveSheet().getValue(0, col) || "")
        //       .trim()
        //       .toLowerCase() === "input" ||
        //     (spread.getActiveSheet().getValue(0, col) || "")
        //       .trim()
        //       .toLowerCase() === "data validation"
        //   ) {
        //     // Protect the column if the value is not "Input"
        //     for (
        //       let row = 9;
        //       row < spread.getActiveSheet().getRowCount();
        //       row++
        //     ) {
        //       spread.getActiveSheet().getCell(row, col).locked(false);
        //     }
        //   }
        // }
      } else {
        spread.options.tabStripVisible = false;
      }

      spread.repaint();
      // Initialize the formula text box
      const formulaTextBox = new GC.Spread.Sheets.FormulaTextBox.FormulaTextBox(
        document.getElementById("formulaTextBoxContainer") ??
          document.createElement("div"),
      );
      formulaTextBox.workbook(spread);

      this.setState((prevState: any) => ({
        ...prevState,
        activeTab: activeTab,
        WorkbookData: workbookObj,
      }));
    });
  };

  addRowClick = (rowCount: number) => {
    const spread = this.spread;
    if (!spread || rowCount <= 0) return;

    const sheet = spread.getActiveSheet();

    const currentRowCount = sheet.getRowCount();
    const templateRowIndex = currentRowCount - 1;

    sheet.addRows(currentRowCount, rowCount);

    for (let i = 0; i < rowCount; i++) {
      const targetRowIndex = currentRowCount + i;

      sheet.copyTo(
        templateRowIndex,
        0,
        targetRowIndex,
        0,
        1,
        sheet.getColumnCount(),
        GC.Spread.Sheets.CopyToOptions.style |
          GC.Spread.Sheets.CopyToOptions.conditionalFormat,
      );

      sheet
        .getRange(targetRowIndex, 0, 1, sheet.getColumnCount())
        .locked(false);

      sheet.autoFitRow(targetRowIndex);
    }

    message.success(`${rowCount} rows added`);
  };

  saveClick = async (e?: any, fromPopup?: boolean) => {
    try {
      // this.setState({ saveButtonLoading: true, showOverlay: true });

      // Getting the sheet related data from the workbook
      const spread: any = this.spread;
      const workbookJson = spread.toJSON();
      const { sheets } = workbookJson;
      const sheetNames = Object.keys(sheets);

      const { sheetId, deliverableId } = this.props.match.params;

      const { activeSheetJson, fileName } = this.getActiveSheetData(
        sheetId,
        workbookJson,
        sheetNames,
      );

      if (!activeSheetJson) {
        return;
      }

      await this.saveWorkbook(
        activeSheetJson,
        fileName,
        deliverableId,
        sheetId,
        spread,
        fromPopup,
      );
      this.spread?.getActiveSheet().clearPendingChanges();

      if (window.location.pathname.includes("/deliverable")) {
        this.setState({
          saveButtonLoading: true,
          showOverlay: true,
          taskStatusMessage: SAVE_TASK_PENDING_MESSAGE,
        });
        // Start polling
        this.setShowOkButton(false);
        this.startPolling("Save");
      } else {
        message.success("File updated successfully");
      }
    } catch (e: any) {
      message.error(SAVE_ERROR);
      this.setState({ saveButtonLoading: false, showOverlay: false });
    }
  };

  startPolling = (taskName: string = "import") => {
    if (!this.poll) {
      // Check if polling is already started
      this.poll = setInterval(() => {
        this.pollForTaskStatus(null, this.state.taskId, taskName);
      }, TASK_POLLING_INTERVAL);
    }
  };

  setTaskStatus = (message: string) => {
    this.setState({ taskStatusMessage: message });
    if (message === IMPORT_SUCCESS_MESSAGE || message === SAVE_SUCCESS) {
      this.setShowOkButton(true);
    }
  };

  setShowOkButton = (show: boolean) => {
    this.setState({ showOkButton: show });
  };

  pollForTaskStatus = (
    successCallback: Function | null,
    taskid: number,
    taskName: string = "import",
  ) => {
    return new Promise<void>(async (resolve, reject) => {
      const interval = parseInt(TASK_POLLING_INTERVAL.toString());

      const handleTaskStatus = (status: string) => {
        switch (status) {
          case TaskStatus.COMPLETED:
            clearInterval(this.poll);
            this.poll = null;
            if (successCallback) successCallback();
            taskName === "Save"
              ? this.setTaskStatus(SAVE_SUCCESS)
              : this.setTaskStatus(IMPORT_SUCCESS_MESSAGE);
            resolve();
            this.clearPollingTimer();
            break;
          case TaskStatus.FAILED:
            clearInterval(this.poll);
            this.poll = null;
            taskName === "Save"
              ? this.setTaskStatus(SAVE_ERROR)
              : this.setTaskStatus(IMPORT_FAILED_MESSAGE);
            this.setShowOkButton(true);
            break;
          default:
            taskName === "Save"
              ? this.setTaskStatus(`${SAVE_TASK_PENDING_MESSAGE}`)
              : this.setTaskStatus(`${IMPORT_TASK_PENDING_MESSAGE}`);
            break;
        }
      };

      const fetchTaskStatus = async () => {
        try {
          const { deliverableId, sheetId } = this.props.match.params;
          const taskStatus =
            sheetId === TEMPLATE
              ? await this.deliverableService.taskStatus<any>(
                  deliverableId,
                  undefined,
                  taskid,
                  TaskType.RERUN,
                )
              : await this.deliverableService.taskStatus<any>(
                  deliverableId,
                  sheetId,
                  taskid,
                );

          if (!taskStatus) {
            if (successCallback) successCallback();
            this.setTaskStatus(TaskStatus.NO_TASK_FOUND);
            clearInterval(this.poll);
            return;
          }

          const status = (taskStatus?.Status ?? "").toLowerCase();
          handleTaskStatus(status);
        } catch (error) {
          clearInterval(this.poll);
          this.clearPollingTimer();
          return;
        }
      };

      // Call fetchTaskStatus immediately and then at intervals
      await fetchTaskStatus();

      this.poll = setInterval(() => {
        fetchTaskStatus().catch((error) => {
          clearInterval(this.poll);
          return;
        });
      }, interval);
    });
  };

  getActiveSheetData(
    sheetId: any,
    workbookJson: any,
    sheetNames: string[],
  ): { activeSheetJson: any; fileName: string } {
    let activeSheetJson: any;
    let fileName: string;

    if (!isNaN(sheetId)) {
      const activeSheetName = this.getActiveSheetName(sheetNames, workbookJson);
      activeSheetJson = { ...workbookJson };
      fileName = `${activeSheetName}.xlsx`;
    } else {
      const params = new URLSearchParams(window.location.search);
      const activeTab = params.get(NAME_KEY);
      activeSheetJson = workbookJson;
      fileName = `${activeTab}.xlsx`;
    }

    return { activeSheetJson, fileName };
  }

  getActiveSheetName(sheetNames: string[], workbookJson: any): string {
    for (const sheetName of sheetNames) {
      if (workbookJson.sheets[sheetName].isSelected) {
        return sheetName;
      }
    }
    return "";
  }

  saveWorkbook = async (
    activeSheetJson: any,
    fileName: string,
    deliverableId: any,
    sheetId: any,
    spread: any,
    fromPopup?: boolean,
  ) => {
    const excelIo = new IO();
    excelIo.save(activeSheetJson, async (blob: any) => {
      const file = new File([blob], fileName, {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      if (file) {
        let response;

        const path = window.location.pathname;

        if (path.includes("/deliverable")) {
          response = await this.deliverableService.import<any>(
            deliverableId,
            sheetId,
            file,
          );
        } else if (path.includes("/template")) {
          response = await this.templateService.importTemplate(
            deliverableId,
            file,
          );
        } else if (path.includes("/coa")) {
          response = await this.coaService.importCoa(
            deliverableId,
            file,
          );
        }

        const responseBlob = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const responseExcelFile = new File([responseBlob], "response.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        if (fromPopup && this.nextSheet) {
          spread.setActiveSheet(this.nextSheet.name());
          this.nextSheet = null;
        }
        this.openFile(this.createFileList(responseExcelFile));
        this.setState({ saveButtonLoading: false, displayPopup: false });
      }
    });
  };

  discardChanges = async (prevProps: any) => {
    const oldSheet = this.previousSheet;

    // Revert dirty rows to their original state
    for (const row in this.dirtyRowData) {
      if (this.dirtyRowData.hasOwnProperty(row)) {
        const rowData = this.dirtyRowData[row];
        oldSheet.setArray(parseInt(row), 0, rowData);
      }
    }

    // Clear the dirty status
    oldSheet.clearPendingChanges();

    // Clear the tracked dirty row data
    this.dirtyRowData = {};
    await this.loadExcel().then(() => {
      this.spread?.setActiveSheet(this.nextSheet.name());
    });
    // Allow navigation to the new sheet
    if (this.nextSheet) {
      this.spread?.setActiveSheet(this.nextSheet.name());
    }

    this.setState((prevState: any) => ({
      ...prevState,
      displayPopup: false,
    }));
  };

  closePopupWithoutSaving = () => {
    this.setState({ displayPopup: false }, () => {
      // Prevent navigation to the new sheet
      if (this.previousSheet) {
        this.spread?.setActiveSheet(this.previousSheet.name());
        this.nextSheet = null;
      }
    });
  };

  convertResponseToBlob = async (deliverableId: number, sheetId: number) => {
    try {
      this.setState({ loading: true });

      const pathname = window.location.pathname;

      let response: AxiosResponse<Blob> | ArrayBuffer | Blob | undefined = {} as any;

      if (pathname.includes("/deliverable") && pathname.includes("template")) {
        response =
          await this.deliverableService.getDeliverableTemplateUsingAxios(
            deliverableId,
          );
      } else if (pathname.includes("/deliverable")) {
        response = await this.deliverableService.getTemplateUsingAxios(
          deliverableId,
          sheetId,
        );
      } else if (pathname.includes("/coa")) {
        response = await this.coaService.getCoaTemplateUsingAxios(deliverableId);
      } else if (pathname.includes("/template")) {
        response = await this.templateService.getRuleEngineTemplateUsingAxios(deliverableId);
      }

      if (response) {
        const responseData =
          typeof response === "object" && "data" in response ? response.data : response;
        const blob = new Blob([responseData], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const file = new File([blob], "example.xlsx", {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        return file;
      }
    } catch (error) {
    } finally {
      this.setState({ loading: false }); // Stop loading
    }

    return null;
  };

  createFileList = (file: File): FileList => {
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    return dataTransfer.files;
  };

  initSpread(spread: any) {
    const search = window.location.search;
    const multiSheet = search.includes("multiSheet");
    this.spread = spread;
    if (!this.statusBar) {
      const container = document.getElementById("statusBar");
      if (container) {
        this.statusBar = new GC.Spread.Sheets.StatusBar.StatusBar(container);
      }
    }
    this.statusBar?.bind(spread);
    spread.suspendPaint();
    this.dirtyRowData = {};
    spread.options.showRowHeader = false;
    spread.options.showColumnHeader = false;
    const sheet = spread.getActiveSheet();
    sheet.options.rowHeaderVisible = false;
    sheet.options.colHeaderVisible = false;
    sheet.frozenRowCount(0);
    sheet.frozenColumnCount(0);
    sheet.setRowCount(500);
    const spreadNS = GC.Spread.Sheets;
    const columnCount = this.spread?.getActiveSheet().getColumnCount() || 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const rowCount = this.spread?.getActiveSheet().getRowCount() || 0;
    for (let col = 0; col < columnCount; col++) {
      //Commenting the code to check column autofit and width issue.
      //this.spread?.getActiveSheet().autoFitColumn(col);
      const colunmWidth = this.spread?.getActiveSheet().getColumnWidth(col);
      //this.spread?.getActiveSheet().setColumnWidth(col, colunmWidth * 1.25);
      this.spread?.getActiveSheet().setColumnWidth(col, colunmWidth);
    }

    // for (let row = 0; row < rowCount; row++) {
    //   this.spread?.getActiveSheet().autoFitRow(row);
    // }
    spread.bind(spreadNS.Events.ActiveSheetChanged, (e: any, args: any) => {
      const oldSheet = args.oldSheet;
      const newSheet = args.newSheet;
      const dirtyRows = oldSheet.getDirtyRows();
      if (multiSheet || (dirtyRows.length > 0 && !multiSheet)) {
        this.previousSheet = oldSheet;
        this.nextSheet = newSheet;
        sheet.setRowCount(500);
        if (dirtyRows.length > 0 && !multiSheet) {
          dirtyRows.forEach((row: any) => {
            this.dirtyRowData[row] = oldSheet.getArray(
              row,
              0,
              1,
              oldSheet.getColumnCount(),
            );
          });
        }

        // Display the pop-up
        this.setState((prevState: any) => ({
          ...prevState,
          displayPopup: true,
        }));

        // Prevent navigation to the new sheet
        spread.setActiveSheet(oldSheet.name());
        const formulaTextBox =
          new GC.Spread.Sheets.FormulaTextBox.FormulaTextBox(
            document.getElementById("formulaContainer") ??
              document.createElement("div"),
          );
        formulaTextBox.workbook(spread);
      } else {
        this.previousSheet = newSheet;
        const newSheetName = newSheet.name();
        const path = window.location.pathname;
        if (!(path.includes("/deliverable") && path.includes("template"))) {
          this.props.updateActiveTab(newSheetName);
        }
      }
    });

    spread.resumePaint();
  }

  loadExcel = async () => {
    const { deliverableId, sheetId } = this.props.match.params;
    this.setState({ importButtonLoading: false });
    if (deliverableId !== "-1") {
      const deliverableNumberId = Number(deliverableId);
      const sheetNumberId = Number(sheetId);
      const data = await this.convertResponseToBlob(
        deliverableNumberId,
        sheetNumberId,
      );
      if (data !== null) {
        this.openFile(this.createFileList(data));
      }
    }
  };
}
